import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { dimensionLabel, unitShort, formatValue, bestAttempt, aggregateResults } from "@/lib/performanceTests";
import { formatDateDE } from "@/lib/playerConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Minus, Save, X, Crown, Ban, Pencil,
} from "lucide-react";
import { toast } from "sonner";

const DIM_ACCENT = {
  technik: "border-sky-500/40 bg-sky-500/5 text-sky-300",
  schnelligkeit: "border-amber-500/40 bg-amber-500/5 text-amber-300",
  ausdauer: "border-emerald-500/40 bg-emerald-500/5 text-emerald-300",
  taktik: "border-fuchsia-500/40 bg-fuchsia-500/5 text-fuchsia-300",
};

export default function PerformanceTestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({}); // { player_id: { attempts: [{value, fehlversuch}] } }
  const [editorPlayer, setEditorPlayer] = useState(null);
  const [paramsOpen, setParamsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, pRes] = await Promise.all([
        api.get(`/performance-tests/${id}`),
        api.get("/players"),
      ]);
      setTest(tRes.data);
      setPlayers(pRes.data);
      // Initialize editing state from results
      const init = {};
      for (const p of pRes.data) {
        const existing = (tRes.data.results || {})[p.id];
        if (existing) {
          init[p.id] = {
            attempts: Array.from({ length: tRes.data.attempts_count }).map((_, i) =>
              existing.attempts?.[i] || { value: null, fehlversuch: false }
            ),
          };
        } else {
          init[p.id] = {
            attempts: Array.from({ length: tRes.data.attempts_count }).map(() => ({ value: null, fehlversuch: false })),
          };
        }
      }
      setEditing(init);
    } catch {
      toast.error("Leistungstest nicht gefunden");
      navigate("/leistungstest");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [id]);

  // Build results-equivalent state for live aggregation (uses editing not test.results)
  const liveTest = useMemo(() => {
    if (!test) return null;
    const results = {};
    for (const [pid, val] of Object.entries(editing)) {
      const hasAttempt = val.attempts.some((a) => a.value != null || a.fehlversuch);
      if (hasAttempt) results[pid] = val;
    }
    return { ...test, results };
  }, [test, editing]);

  const agg = useMemo(() => (liveTest ? aggregateResults(liveTest, players) : null), [liveTest, players]);

  // Direct save handler — invoked by the editor when user clicks "Speichern".
  // Persists the current `editing` state for the given player and reloads the test.
  const persistPlayer = async (playerId, attempts) => {
    // Build full results payload from current editing + this player's new attempts
    const merged = { ...editing, [playerId]: { attempts } };
    const cleaned = {};
    for (const [pid, val] of Object.entries(merged)) {
      const hasAttempt = val.attempts.some((a) => a.value != null || a.fehlversuch);
      if (hasAttempt) {
        cleaned[pid] = {
          groesse_cm: null,
          gewicht_kg: null,
          attempts: val.attempts.map((a) => ({
            value: a.fehlversuch ? null : a.value,
            fehlversuch: !!a.fehlversuch,
          })),
        };
      }
    }
    const { data } = await api.put(`/performance-tests/${id}`, { results: cleaned });
    setTest(data);
    // Sync local editing for this player so the row reflects the saved state
    setEditing((prev) => ({ ...prev, [playerId]: { attempts } }));
    return data;
  };

  if (loading || !test) return <div className="max-w-3xl mx-auto p-6 text-zinc-500 text-sm">Lädt…</div>;

  const sortedPlayers = [...players].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="lt-detail-page">
      <button
        onClick={() => navigate("/leistungstest")}
        className="text-xs text-zinc-400 hover:text-brand-lighter transition flex items-center gap-1 mb-4"
        data-testid="lt-detail-back"
      >
        <ArrowLeft className="h-3 w-3" /> Zurück zur Übersicht
      </button>

      {/* Header */}
      <div className={`rounded-lg border p-4 sm:p-5 mb-4 relative ${DIM_ACCENT[test.dimension] || "border-zinc-800 bg-zinc-900/40"}`}>
        <button
          type="button"
          onClick={() => setParamsOpen(true)}
          className="absolute top-3 right-3 z-10 h-9 w-9 rounded-md flex items-center justify-center text-zinc-300 hover:text-brand-lighter hover:bg-zinc-900 transition"
          data-testid="lt-edit-params-btn"
          aria-label="Test-Parameter bearbeiten"
          title="Test-Parameter bearbeiten"
        >
          <Pencil className="h-4 w-4" />
        </button>
        <div className="text-[10px] uppercase tracking-wider opacity-70">{dimensionLabel(test.dimension)}</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-zinc-100 pr-10">{test.test_name}</h1>
        <div className="text-xs text-zinc-400 mt-1">
          {formatDateDE(test.datum)}
          {test.uhrzeit_start && <> · {test.uhrzeit_start}{test.uhrzeit_ende ? ` – ${test.uhrzeit_ende}` : ""}</>}
          {" · "}{test.attempts_count} Versuche · Einheit {unitShort(test.unit)} ({test.direction === "lower" ? "↓ niedriger besser" : "↑ höher besser"})
        </div>
        {test.description && (
          <div className="mt-2 text-xs text-zinc-300 whitespace-pre-wrap">{test.description}</div>
        )}
      </div>

      {/* Live aggregate */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4" data-testid="lt-agg-avg">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Team-Ø (ohne Fehlversuche)</div>
          <div className="font-display text-2xl font-bold text-zinc-100 tabular-nums mt-1">
            {agg?.avg == null ? "—" : formatValue(agg.avg, test.unit)}
          </div>
          <div className="text-[10px] text-zinc-500 mt-0.5">{agg?.count || 0} Spieler gewertet</div>
        </div>
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4" data-testid="lt-agg-top">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
            <Crown className="h-3 w-3 text-amber-400" /> Team-Bestwert
          </div>
          {agg?.top ? (
            <>
              <div className="font-display text-2xl font-bold text-brand-lighter tabular-nums mt-1">
                {formatValue(agg.top.bestValue, test.unit)}
              </div>
              <div className="text-[11px] text-zinc-400 mt-0.5 truncate">{agg.top.player.name}</div>
            </>
          ) : (
            <div className="font-display text-2xl font-bold text-zinc-600 mt-1">—</div>
          )}
        </div>
      </div>

      {/* Ranking + Player rows */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 divide-y divide-zinc-800" data-testid="lt-ranking">
        {sortedPlayers.map((p) => {
          const data = editing[p.id] || { attempts: [], groesse_cm: null, gewicht_kg: null };
          const ba = bestAttempt(data.attempts, test.direction);
          const rank = agg?.ranking?.findIndex((r) => r.player.id === p.id);
          const rankPos = rank != null && rank >= 0 ? rank + 1 : null;

          return (
            <div key={p.id} className="p-3 sm:p-4" data-testid={`lt-row-${p.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className={
                    "h-9 w-9 rounded-md font-display font-bold text-sm flex items-center justify-center shrink-0 " +
                    (rankPos === 1 ? "bg-amber-500/20 text-amber-300"
                      : rankPos === 2 ? "bg-zinc-700/40 text-zinc-300"
                      : rankPos === 3 ? "bg-orange-900/40 text-orange-300"
                      : "bg-zinc-900 text-zinc-500 border border-zinc-800")
                  }>
                    {rankPos || "—"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-zinc-100 truncate">{p.name}</div>
                    <div className="text-[11px] text-zinc-500">
                      Best: {ba ? <span className="text-brand-lighter font-semibold">{formatValue(ba.value, test.unit)}</span> : "—"}
                    </div>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditorPlayer(p)}
                  className="bg-transparent border-zinc-700 text-zinc-200 hover:text-brand-lighter hover:bg-zinc-900 rounded-md"
                  data-testid={`lt-row-edit-${p.id}`}
                >
                  Erfassen
                </Button>
              </div>
            </div>
          );
        })}
        {sortedPlayers.length === 0 && (
          <div className="p-6 text-center text-sm text-zinc-500">Keine Spieler im Kader.</div>
        )}
      </div>

      <PlayerResultEditor
        player={editorPlayer}
        test={test}
        data={editorPlayer ? editing[editorPlayer.id] : null}
        onClose={() => setEditorPlayer(null)}
        onSave={async (attempts) => {
          if (!editorPlayer) return;
          try {
            await persistPlayer(editorPlayer.id, attempts);
            toast.success("Ergebnisse gespeichert");
            setEditorPlayer(null);
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
            throw e;
          }
        }}
      />

      <ParamsEditDialog
        open={paramsOpen}
        test={test}
        onClose={() => setParamsOpen(false)}
        onSaved={async () => { setParamsOpen(false); await load(); }}
      />
    </div>
  );
}

function ParamsEditDialog({ open, test, onClose, onSaved }) {
  const [datum, setDatum] = useState("");
  const [uhrzeitStart, setUhrzeitStart] = useState("");
  const [uhrzeitEnde, setUhrzeitEnde] = useState("");
  const [attemptsCount, setAttemptsCount] = useState(2);
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && test) {
      setDatum(test.datum || "");
      setUhrzeitStart(test.uhrzeit_start || "");
      setUhrzeitEnde(test.uhrzeit_ende || "");
      setAttemptsCount(test.attempts_count || 2);
      setDescription(test.description || "");
    }
  }, [open, test]);

  if (!open || !test) return null;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/performance-tests/${test.id}`, {
        datum,
        attempts_count: attemptsCount,
        uhrzeit_start: uhrzeitStart || null,
        uhrzeit_ende: uhrzeitEnde || null,
        description,
        clear_description: !description,
      });
      toast.success("Parameter aktualisiert");
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-lg font-semibold tracking-tight">Test-Parameter bearbeiten</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">{test.test_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-xs text-zinc-400">Datum</Label>
            <Input type="date" value={datum} onChange={(e) => setDatum(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 mt-1 h-11 rounded-md focus-visible:ring-brand"
              data-testid="lt-params-datum" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Uhrzeit (Start – Ende, optional)</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Input type="time" value={uhrzeitStart} onChange={(e) => setUhrzeitStart(e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 h-11 rounded-md focus-visible:ring-brand"
                data-testid="lt-params-time-start" />
              <Input type="time" value={uhrzeitEnde} onChange={(e) => setUhrzeitEnde(e.target.value)}
                className="bg-zinc-900 border-zinc-800 text-zinc-100 h-11 rounded-md focus-visible:ring-brand"
                data-testid="lt-params-time-end" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Anzahl Versuche</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {[1, 2, 3].map((n) => (
                <button key={n} type="button" onClick={() => setAttemptsCount(n)}
                  className={
                    "min-h-[40px] rounded-md border text-sm font-medium transition " +
                    (attemptsCount === n
                      ? "bg-brand border-brand text-white"
                      : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-700")
                  }
                  data-testid={`lt-params-attempts-${n}`}
                >{n}</button>
              ))}
            </div>
            {attemptsCount !== test.attempts_count && (
              <div className="text-[11px] text-amber-400 mt-1">
                Hinweis: Bestehende Versuche bleiben erhalten; zusätzliche Slots erscheinen leer.
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Beschreibung / Notiz</Label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Optional — z. B. Hinweise zur Durchführung"
              className="w-full bg-zinc-900 border border-zinc-800 text-zinc-100 mt-1 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              data-testid="lt-params-description"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
            className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-900 rounded-md"
            data-testid="lt-params-cancel">Abbrechen</Button>
          <Button onClick={save} disabled={saving}
            className="bg-brand hover:bg-brand-light text-white rounded-md"
            data-testid="lt-params-save">{saving ? "Speichern…" : "Speichern"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// PlayerResultEditor — Mobile-optimiertes Ergebnis-Erfassungs-Modal
// -----------------------------------------------------------------------------
function PlayerResultEditor({ player, test, data, onClose, onSave }) {
  const [attempts, setAttempts] = useState([]);
  const [saving, setSaving] = useState(false);

  // Re-init attempts whenever a new player is opened
  useEffect(() => {
    if (player && data) setAttempts(data.attempts.map((a) => ({ ...a })));
    else setAttempts([]);
  }, [player, data]);

  if (!player || !test) return null;
  const unit = test.unit;

  const setAttempt = (idx, patch) => {
    setAttempts((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(attempts);
    } catch {
      // Toast already shown by parent
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!player} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md p-0 gap-0 flex flex-col max-h-[100dvh] sm:max-h-[85vh] h-[100dvh] sm:h-auto sm:rounded-lg rounded-none"
      >
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-zinc-800 shrink-0">
          <DialogTitle className="font-display text-lg font-semibold tracking-tight">{player.name}</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">{test.test_name} · {formatDateDE(test.datum)}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3" data-testid="lt-edit-attempts-scroll">
          {attempts.map((a, idx) => (
            <AttemptInput
              key={idx}
              idx={idx}
              attempt={a}
              unit={unit}
              onChange={(patch) => setAttempt(idx, patch)}
            />
          ))}
        </div>

        {/* Sticky Footer — immer sichtbar, auch bei vielen Versuchen */}
        <div
          className="px-6 py-4 border-t border-zinc-800 bg-zinc-950 shrink-0"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-brand hover:bg-brand-light text-white rounded-md w-full h-12 text-base font-semibold"
            data-testid="lt-edit-save"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AttemptInput({ idx, attempt, unit, onChange }) {
  const isCount = unit === "count" || unit === "level";

  const setValue = (v) => onChange({ value: v, fehlversuch: false });
  const toggleFail = () => {
    if (attempt.fehlversuch) {
      onChange({ fehlversuch: false });
    } else {
      onChange({ fehlversuch: true, value: null });
    }
  };

  return (
    <div className={
      "rounded-md border p-3 " +
      (attempt.fehlversuch
        ? "border-rose-500/40 bg-rose-500/5"
        : attempt.value != null ? "border-brand/40 bg-brand/5" : "border-zinc-800 bg-zinc-900/40")
    } data-testid={`lt-attempt-${idx}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-wider text-zinc-400">Versuch {idx + 1}</div>
        <button
          type="button"
          onClick={toggleFail}
          className={
            "text-[11px] uppercase tracking-wider px-2 py-1 rounded transition flex items-center gap-1 " +
            (attempt.fehlversuch
              ? "bg-rose-500 text-white"
              : "text-zinc-400 hover:text-rose-400 hover:bg-zinc-900")
          }
          data-testid={`lt-attempt-fail-${idx}`}
        >
          <Ban className="h-3 w-3" /> Fehlversuch
        </button>
      </div>
      {attempt.fehlversuch ? (
        <div className="text-rose-400 text-sm font-semibold flex items-center gap-1">
          <X className="h-4 w-4" /> Fehlversuch — wird nicht gewertet
        </div>
      ) : isCount ? (
        <CountStepper value={attempt.value} unit={unit} onChange={setValue} testId={`lt-counter-${idx}`} />
      ) : (
        <TimeStepper value={attempt.value} onChange={setValue} testId={`lt-timer-${idx}`} />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Count / Level stepper (+/-)
// -----------------------------------------------------------------------------
function CountStepper({ value, unit, onChange, testId }) {
  const v = value ?? 0;
  const inc = (delta) => onChange(Math.max(0, v + delta));
  const stepLarge = unit === "level" ? 1 : 5;
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <button
        type="button"
        onClick={() => inc(-stepLarge)}
        className="h-12 w-12 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200 font-display text-sm font-bold"
        data-testid={`${testId}-minus-lg`}
        aria-label={`Minus ${stepLarge}`}
      >
        −{stepLarge}
      </button>
      <button
        type="button"
        onClick={() => inc(-1)}
        className="h-12 w-12 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-200"
        data-testid={`${testId}-minus`}
        aria-label="Minus 1"
      >
        <Minus className="h-5 w-5 mx-auto" />
      </button>
      <div className="flex-1 text-center font-display text-3xl font-bold text-zinc-100 tabular-nums">
        {value == null ? <span className="text-zinc-600">—</span> : value}
        {unit === "level" && value != null && <span className="text-base text-zinc-500 ml-1">Lvl</span>}
      </div>
      <button
        type="button"
        onClick={() => inc(1)}
        className="h-12 w-12 rounded-md bg-brand hover:bg-brand-light text-white"
        data-testid={`${testId}-plus`}
        aria-label="Plus 1"
      >
        <Plus className="h-5 w-5 mx-auto" />
      </button>
      <button
        type="button"
        onClick={() => inc(stepLarge)}
        className="h-12 w-12 rounded-md bg-brand hover:bg-brand-light text-white font-display text-sm font-bold"
        data-testid={`${testId}-plus-lg`}
        aria-label={`Plus ${stepLarge}`}
      >
        +{stepLarge}
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Time stepper for seconds.hundredths
// -----------------------------------------------------------------------------
function TimeStepper({ value, onChange, testId }) {
  const v = value ?? 0;
  // Split into seconds and hundredths
  const sec = Math.floor(v);
  const hund = Math.round((v - sec) * 100);

  const set = (newSec, newHund) => {
    const s = Math.max(0, newSec);
    const h = ((newHund % 100) + 100) % 100;
    onChange(Math.round((s + h / 100) * 100) / 100);
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="text-center font-display text-4xl font-bold text-zinc-100 tabular-nums" data-testid={`${testId}-display`}>
        {value == null ? <span className="text-zinc-600 text-3xl">—</span> : (
          <>
            <span>{String(sec).padStart(2, "0")}</span>
            <span className="text-zinc-500">.</span>
            <span>{String(hund).padStart(2, "0")}</span>
            <span className="text-base text-zinc-500 ml-1">s</span>
          </>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 text-center mb-1">Sekunden</div>
          <div className="grid grid-cols-3 gap-1">
            <button type="button" onClick={() => set(sec - 1, hund)}
              className="h-10 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200" data-testid={`${testId}-sec-minus`}>−1</button>
            <div className="h-10 rounded bg-zinc-900 border border-zinc-800 text-zinc-100 font-bold flex items-center justify-center tabular-nums">
              {String(sec).padStart(2, "0")}
            </div>
            <button type="button" onClick={() => set(sec + 1, hund)}
              className="h-10 rounded bg-brand hover:bg-brand-light text-white" data-testid={`${testId}-sec-plus`}>+1</button>
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 text-center mb-1">Hundertstel</div>
          <div className="grid grid-cols-3 gap-1">
            <button type="button" onClick={() => set(sec, hund - 1)}
              className="h-10 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200" data-testid={`${testId}-hund-minus`}>−1</button>
            <div className="h-10 rounded bg-zinc-900 border border-zinc-800 text-zinc-100 font-bold flex items-center justify-center tabular-nums">
              {String(hund).padStart(2, "0")}
            </div>
            <button type="button" onClick={() => set(sec, hund + 1)}
              className="h-10 rounded bg-brand hover:bg-brand-light text-white" data-testid={`${testId}-hund-plus`}>+1</button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-1">
        {[0.1, 0.5, 1.0, 5.0].map((step) => (
          <button
            key={step}
            type="button"
            onClick={() => onChange(Math.round((v + step) * 100) / 100)}
            className="h-9 rounded bg-zinc-900 border border-zinc-800 text-zinc-300 hover:border-brand-lighter/40 text-xs"
            data-testid={`${testId}-step-${step}`}
          >
            +{step.toFixed(step < 1 ? 1 : 0)}s
          </button>
        ))}
      </div>
      {value != null && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="w-full h-8 text-[11px] text-zinc-500 hover:text-rose-400"
          data-testid={`${testId}-clear`}
        >
          Wert löschen
        </button>
      )}
    </div>
  );
}
